import type { DesktopUpdateActionResult, DesktopUpdateState } from "@t3tools/contracts";

import { APP_BASE_NAME } from "../branding";
import { t } from "~/i18n";

export type DesktopUpdateButtonAction = "download" | "install" | "none";

export function resolveDesktopUpdateButtonAction(
  state: DesktopUpdateState,
): DesktopUpdateButtonAction {
  if (state.downloadedVersion) {
    return "install";
  }
  if (state.status === "available") {
    return "download";
  }
  if (state.status === "error") {
    if (state.errorContext === "download" && state.availableVersion) {
      return "download";
    }
  }
  return "none";
}

export function shouldShowDesktopUpdateButton(state: DesktopUpdateState | null): boolean {
  if (!state || !state.enabled) {
    return false;
  }
  if (state.status === "downloading") {
    return true;
  }
  return resolveDesktopUpdateButtonAction(state) !== "none";
}

export function shouldShowArm64IntelBuildWarning(state: DesktopUpdateState | null): boolean {
  return state?.hostArch === "arm64" && state.appArch === "x64";
}

export function isDesktopUpdateButtonDisabled(state: DesktopUpdateState | null): boolean {
  return state?.status === "downloading";
}

export function getArm64IntelBuildWarningDescription(state: DesktopUpdateState): string {
  if (!shouldShowArm64IntelBuildWarning(state)) {
    return t("common/thisInstallIsUsing_39lnb2");
  }

  const action = resolveDesktopUpdateButtonAction(state);
  if (action === "download") {
    return t("common/thisMacHasApple_iadu2f", { APP_BASE_NAME });
  }
  if (action === "install") {
    return t("common/thisMacHasApple_cnqu77", { APP_BASE_NAME });
  }
  return t("common/thisMacHasApple_5i7nly", { APP_BASE_NAME });
}

export function getDesktopUpdateButtonTooltip(state: DesktopUpdateState): string {
  if (state.status === "available") {
    return state.availableVersion
      ? t("common/updateReadyToDownload_tw9a3r", { "0": state.availableVersion })
      : t("common/updateReadyToDownload_uxvc0i");
  }
  if (state.status === "downloading") {
    const progress =
      typeof state.downloadPercent === "number" ? ` (${Math.floor(state.downloadPercent)}%)` : "";
    return t("common/downloadingUpdate_q6r57c", { progress });
  }
  if (state.status === "downloaded") {
    const downloadedVersion = state.downloadedVersion ?? state.availableVersion;
    return downloadedVersion
      ? t("common/updateDownloadedClickTo_848rd1", { downloadedVersion })
      : t("common/updateDownloadedClickTo_8qca3o");
  }
  if (state.status === "error") {
    if (state.errorContext === "download" && state.availableVersion) {
      return t("common/downloadFailedForClick_wyj3hj", { "0": state.availableVersion });
    }
    if (state.errorContext === "install" && state.downloadedVersion) {
      return t("common/installFailedForClick_ondt2d", { "0": state.downloadedVersion });
    }
    return state.message ?? t("common/updateFailed_uafrzn");
  }
  return t("common/upToDate_rxx2l7");
}

export function getDesktopUpdateInstallConfirmationMessage(
  state: Pick<DesktopUpdateState, "availableVersion" | "downloadedVersion">,
): string {
  const version = state.downloadedVersion ?? state.availableVersion;
  return version
    ? t("common/installUpdateAndRestart_gvd618", { version, APP_BASE_NAME })
    : t("common/installUpdateAndRestart_b975d3", { APP_BASE_NAME });
}

export function getDesktopUpdateActionError(result: DesktopUpdateActionResult): string | null {
  if (!result.accepted || result.completed) return null;
  if (typeof result.state.message !== "string") return null;
  const message = result.state.message.trim();
  return message.length > 0 ? message : null;
}

export function shouldToastDesktopUpdateActionResult(result: DesktopUpdateActionResult): boolean {
  return getDesktopUpdateActionError(result) !== null;
}

export function shouldHighlightDesktopUpdateError(state: DesktopUpdateState | null): boolean {
  if (!state || state.status !== "error") return false;
  return state.errorContext === "download" || state.errorContext === "install";
}

export function canCheckForUpdate(state: DesktopUpdateState | null): boolean {
  if (!state || !state.enabled) return false;
  return (
    state.status !== "checking" &&
    state.status !== "downloading" &&
    state.status !== "downloaded" &&
    state.status !== "disabled"
  );
}
